/**
 * Parses Greenhouse hostname prefixes at the user-input boundary.
 *
 * The parser trims and lowercases valid DNS labels, then returns a branded value or a typed error.
 * GreenhouseOauthSubdomainDialog is the next file in the flow and uses the branded value for save
 * and OAuth actions.
 */

import { err, ok, type Result } from 'neverthrow';

const GREENHOUSE_SUBDOMAIN_PATTERN =
  /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*$/;

declare const greenhouseSubdomainBrand: unique symbol;

/** A normalized Greenhouse hostname prefix, such as `app3`. */
export type GreenhouseSubdomain = string & { readonly [greenhouseSubdomainBrand]: true };

/** Describes user input that does not contain valid DNS labels. */
export type InvalidGreenhouseSubdomain = { type: 'INVALID_GREENHOUSE_SUBDOMAIN' };

/** Trims, lowercases, and parses a Greenhouse hostname prefix. */
export function parseGreenhouseSubdomain(value: string): Result<GreenhouseSubdomain, InvalidGreenhouseSubdomain> {
  const normalizedValue = value.trim().toLowerCase();
  return GREENHOUSE_SUBDOMAIN_PATTERN.test(normalizedValue) &&
    normalizedValue !== 'greenhouse.io' &&
    !normalizedValue.endsWith('.greenhouse.io')
    ? ok(normalizedValue as GreenhouseSubdomain)
    : err({ type: 'INVALID_GREENHOUSE_SUBDOMAIN' });
}
